import { useEffect, useMemo, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { useSelector } from 'react-redux';
import {
  FiCalendar,
  FiFileText,
  FiLayers,
  FiSave,
  FiInfo,
} from 'react-icons/fi';
import styles from './Leave.module.scss';
import { Text, textVariantsEnum, fontWeightEnum } from '@/components/Text';
import { Dropdown, borderedSelectStyles } from '@/components/Dropdown';
import { TextArea } from '@/components/TextArea';
import { InitialsBadge } from '@/components/InitialsBadge';
import {
  IconBadge,
  iconBadgeSizes,
  iconBadgeShapes,
} from '@/components/IconBadge';
import { InfoBox } from '@/components/InfoBox';
import { Button, buttonVariants } from '@/components/Button';
import { addNotification, notificationStates } from '@components/Notification';
import { leaveBalanceService } from '@services/leaveBalance';
import firebase from '@config/firebase.config';

import {
  leaveTypeOptions,
  paidLeaveTypeList,
  paidLeaveTypeOptions,
  unpaidLeaveOption,
  paidLeaveOption,
} from './Leave.constants';
import {
  validateLeaveForm,
  toDateString,
  fromDateString,
} from './Leave.helper';
import { LeaveDatePicker } from './LeaveDatePicker.component';

const database = firebase.firestore();
const COMMENT_MAX_LENGTH = 250;

function formatManagerOptionLabel(option) {
  const label = option.userName || option.label;
  return (
    <div className={styles.personCell}>
      <InitialsBadge label={label} twoLetter />
      <div className={styles.personCellText}>
        <Text
          variant={textVariantsEnum.Text_sm}
          fontWeight={fontWeightEnum.Medium}
        >
          {label}
        </Text>
        <Text
          variant={textVariantsEnum.Text_xs}
          fontWeight={fontWeightEnum.Normal}
          color="gray-500"
        >
          {option.role || '—'}
        </Text>
      </div>
    </div>
  );
}

export const UpdateLeaveFormModal = ({
  isVisible,
  leaveData,
  companyManagers,
  onClose,
  fetchLeaveRequestsList,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { activeCompany } = useSelector((state) => state.auth.profile);
  const { profile } = useSelector((state) => state.auth);
  const teams = useSelector((state) => ({
    projects: state.team.projects,
    active: state.team.active,
    teams: state?.team?.teams,
  }));

  const companyManagersDropdownOptions = useMemo(() => {
    return companyManagers
      .map((manager) => ({
        ...manager,
        label: manager.userName,
        value: manager.userUID,
      }))
      .sort((a, b) => (a.label || '').localeCompare(b.label || ''));
  }, [companyManagers]);

  const [formData, setFormData] = useState({
    leaveType: null,
    paidLeaveType: null,
    leaveComment: '',
    leaveManager: null,
    leaveDate: null,
  });

  // Re-syncs whenever a different row is opened for editing — this modal
  // stays mounted between edits, so state can't just be seeded once.
  useEffect(() => {
    if (!leaveData) return;
    const isPaidType = paidLeaveTypeList.includes(leaveData.type);
    setFormData({
      leaveType: isPaidType ? paidLeaveOption : unpaidLeaveOption,
      paidLeaveType: isPaidType
        ? paidLeaveTypeOptions.find((o) => o.value === leaveData.type) || null
        : null,
      leaveComment: leaveData.comment || '',
      leaveManager:
        companyManagersDropdownOptions.find(
          (m) => m.value === leaveData.assignee
        ) || null,
      leaveDate: fromDateString(leaveData.leaveDate),
    });
  }, [leaveData, companyManagersDropdownOptions]);

  const validatePaidLeaveBalance = async () => {
    if (!paidLeaveTypeList.includes(formData.paidLeaveType?.value)) {
      return true;
    }
    try {
      const policy = await leaveBalanceService.getLeavePolicy(
        activeCompany.companyId
      );
      if (!policy) {
        addNotification({
          message:
            'No paid leave policy configured. Please contact your administrator.',
          level: notificationStates.error,
        });
        return false;
      }

      // Editing an already-paid leave shouldn't double-count its own days
      // against the balance it's already allocated from.
      const wasAlreadyPaidType = paidLeaveTypeList.includes(leaveData.type);
      if (
        wasAlreadyPaidType &&
        formData.paidLeaveType?.value === leaveData.type
      ) {
        return true;
      }

      const balance = await leaveBalanceService.getEffectiveRemainingPaidLeave(
        profile.id,
        activeCompany.companyId
      );
      if (!balance.hasPolicy) {
        addNotification({
          message:
            'No paid leave policy configured. Please contact your administrator.',
          level: notificationStates.error,
        });
        return false;
      }
      if (balance.effectiveRemaining <= 0) {
        addNotification({
          message:
            'You have consumed all your available paid leave days. No leave balance remaining.',
          level: notificationStates.error,
        });
        return false;
      }
      return true;
    } catch (error) {
      console.error('Error checking paid leave balance:', error);
      addNotification({
        message: 'Error checking paid leave balance. Please try again.',
        level: notificationStates.error,
      });
      return false;
    }
  };

  const handleUpdate = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    if (!validateLeaveForm(formData)) {
      setIsSubmitting(false);
      return;
    }
    if (!(await validatePaidLeaveBalance())) {
      setIsSubmitting(false);
      return;
    }

    const leaveDate = toDateString(formData.leaveDate);
    const companyLeaveRef = database
      .collection('companyDatabase')
      .doc(activeCompany.companyId)
      .collection('leaveRequests');

    const querySnap = await companyLeaveRef
      .where('userId', '==', profile.id)
      .where('leaveDate', '==', leaveDate)
      .where('status', '!=', 'Declined')
      .get();
    const clashesWithAnotherRequest = querySnap.docs.some(
      (doc) => doc.id !== leaveData.id
    );
    if (clashesWithAnotherRequest) {
      addNotification({
        message: `You already have submitted a request for leave on the date - ${leaveDate}`,
        level: notificationStates.error,
      });
      setIsSubmitting(false);
      return;
    }

    const managerId = formData.leaveManager.value;
    const leaveType =
      formData.leaveType.value === paidLeaveOption.value
        ? formData.paidLeaveType?.value
        : formData.leaveType?.value;
    const finalData = {
      userId: profile.id,
      companyId: activeCompany.companyId,
      companyID: activeCompany.companyId,
      teamId: teams.active?.activeTeamid || leaveData.teamId || '',
      assignee: managerId,
      managerId,
      status: 'Pending',
      type: leaveType || '',
      comment: formData.leaveComment || 'No Comment',
      leaveDate,
      requestDate: new Date(),
    };

    try {
      await companyLeaveRef.doc(leaveData.id).update(finalData);
      addNotification({
        message: 'Request updated successfully',
        level: notificationStates.success,
      });
      setIsSubmitting(false);
      onClose();
      fetchLeaveRequestsList();
    } catch (error) {
      console.error('Error updating leave request:', error);
      addNotification({
        message: 'Request update failed',
        level: notificationStates.error,
      });
      setIsSubmitting(false);
    }
  };

  if (!leaveData) return null;

  return (
    <Dialog
      visible={isVisible}
      onHide={onClose}
      closable={!isSubmitting}
      closeOnEscape={!isSubmitting}
      dismissableMask={!isSubmitting}
      style={{ width: '640px' }}
      header={
        <div className={styles.leaveDialogHeader}>
          <IconBadge
            icon={<FiCalendar size={20} color="var(--indigo-600)" />}
            bg="indigo-100"
            size={iconBadgeSizes.lg}
            shape={iconBadgeShapes.circle}
          />
          <div className={styles.leaveDialogHeaderText}>
            <Text
              variant={textVariantsEnum.Text_lg}
              fontWeight={fontWeightEnum.Semibold}
            >
              Update Leave Request
            </Text>
            <Text
              variant={textVariantsEnum.Text_sm}
              fontWeight={fontWeightEnum.Normal}
              color="gray-600"
            >
              Update the details of your leave request.
            </Text>
          </div>
        </div>
      }
      footer={
        <div className={styles.leaveDialogFooter}>
          <Button
            variant={buttonVariants.secondary}
            text="Cancel"
            onClick={onClose}
            disabled={isSubmitting}
          />
          <Button
            variant={buttonVariants.primary}
            text="Update Request"
            icon={<FiSave size={16} />}
            onClick={handleUpdate}
            loading={isSubmitting}
          />
        </div>
      }
    >
      <div className={styles.leaveFormModalContent}>
        <div className={styles.formRow2Col}>
          <Dropdown
            options={leaveTypeOptions}
            label="Leave Type *"
            value={formData.leaveType}
            selectStyles={borderedSelectStyles}
            leftIcon={<FiFileText size={14} color="var(--indigo-500)" />}
            onChange={(option) =>
              setFormData((prev) => ({ ...prev, leaveType: option }))
            }
          />
          {formData.leaveType?.value === paidLeaveOption.value && (
            <Dropdown
              options={paidLeaveTypeOptions}
              label="Paid Leave Type *"
              value={formData.paidLeaveType}
              selectStyles={borderedSelectStyles}
              leftIcon={<FiLayers size={14} color="var(--indigo-500)" />}
              placeholder="Select leave type"
              onChange={(option) =>
                setFormData((prev) => ({ ...prev, paidLeaveType: option }))
              }
            />
          )}
        </div>

        <div className={styles.datePicker}>
          <Text
            variant={textVariantsEnum.Text_13}
            fontWeight={fontWeightEnum.Normal}
            className={styles.label}
          >
            Leave Date *
          </Text>
          <LeaveDatePicker
            compact
            date={toDateString(formData.leaveDate)}
            onDateChange={(dateString) =>
              setFormData((prev) => ({
                ...prev,
                leaveDate: fromDateString(dateString),
              }))
            }
            placeholder="Select date"
          />
        </div>

        <TextArea
          label="Comment (Optional)"
          placeholder="Add a comment..."
          value={formData.leaveComment}
          maxLength={COMMENT_MAX_LENGTH}
          onChange={(e) =>
            setFormData((prev) => ({ ...prev, leaveComment: e.target.value }))
          }
        />

        <Dropdown
          label="Manager *"
          options={companyManagersDropdownOptions}
          value={formData.leaveManager}
          selectStyles={borderedSelectStyles}
          formatOptionLabel={formatManagerOptionLabel}
          onChange={(option) =>
            setFormData((prev) => ({ ...prev, leaveManager: option }))
          }
        />

        <InfoBox icon={<FiInfo size={16} />}>
          Updating this request will reset its status to Pending and resend it
          to your manager for approval.
        </InfoBox>
      </div>
    </Dialog>
  );
};
